import type { ApiResponse } from "@restaurant/shared";

/**
 * Public error type surfaced to SDK consumers so apps can branch on
 * documented API error codes (see restauran-menu.postman.json) without
 * knowing anything about the underlying HTTP/fetch implementation.
 */
export class AuthApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = "AuthApiError";
  }
}

function parseError(body: unknown, status?: number): AuthApiError {
  if (
    body !== null &&
    typeof body === "object" &&
    "error" in body &&
    body.error !== null &&
    typeof body.error === "object"
  ) {
    const err = body.error as { code?: string; message?: string };
    return new AuthApiError(err.code ?? "UNKNOWN_ERROR", err.message ?? "An unexpected error occurred", status);
  }
  return new AuthApiError("UNKNOWN_ERROR", "An unexpected error occurred", status);
}

export type HttpClientOptions = {
  baseUrl: string;
  getAccessToken: () => string | null;
};

export type RequestOptions = {
  /** Attach `Authorization: Bearer <accessToken>` when true. */
  auth?: boolean;
};

export function createHttpClient(options: HttpClientOptions) {
  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    reqOptions?: RequestOptions
  ): Promise<T> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };

    if (reqOptions?.auth) {
      const token = options.getAccessToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    const init: RequestInit = {
      method,
      headers,
      credentials: "include",
    };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }

    const res = await fetch(`${options.baseUrl}${path}`, init);

    const json = (await res.json().catch(() => null)) as ApiResponse<T> | null;

    if (!res.ok || !json || json.success === false) {
      throw parseError(json, res.status);
    }

    return json.data;
  }

  return {
    get: <T>(path: string, reqOptions?: RequestOptions) => request<T>("GET", path, undefined, reqOptions),
    post: <T>(path: string, body?: unknown, reqOptions?: RequestOptions) =>
      request<T>("POST", path, body, reqOptions),
  };
}

export type HttpClient = ReturnType<typeof createHttpClient>;
